<template>
  <div class="l-block l-mb-6">
    <div class="inbox-area">
      <div class="l-block__header">
        <TitleSub level="h2">
          <Icon2 size="18" variant="search" />
          調査したいキーワード
        </TitleSub>
        <p class="c-text--note">
          データ取得の混雑具合により、データ表示までお時間をいただく場合がございます。
        </p>
      </div>
      <div class="c-alertPos" @click="focusInput">
        <transition-group
          name="v-fade-in"
          tag="div"
          class="c-input-tags"
          :class="{
            alert: (isEdited.input && error.input) || isOver
          }"
        >
          <div
            v-for="(tag, index) in params.tags"
            :key="'tag_' + tag"
            class="c-input-tags__item"
            @click.stop
          >
            <span class="c-input-tags__text">{{ tag }}</span>
            <button
              type="button"
              class="c-input-tags__btn"
              @click="removeTag(index)"
            >
              ×
            </button>
          </div>
          <textarea
            key="input"
            v-model="params.input"
            v-focus
            placeholder="キーワード（語句）を１行に１つずつ、またカンマで区切って入力するか、貼り付けてください。"
            class="c-input-tags__input"
            ref="input"
            @keydown.backspace="removeLastTag"
            @keydown.prevent.enter="enter"
            @paste="paste"
          />
        </transition-group>
        <Alert v-if="isOver">
          一度に調査できるのは{{ limit }}キーワードまでです。{{
            total - limit
          }}キーワード減らしてください
        </Alert>
        <Alert v-else-if="isEdited.input && error.input">
          {{ error.input }}
        </Alert>
      </div>
      <div class="c-alertPos">
        <Button
          size="w230"
          fsize="f16"
          variant="main"
          :disabled="isDisabled"
          :class="{ 'is-loading': isProcessing }"
          @click="submit"
        >
          調査する
        </Button>
        <Alert v-if="hasError" variant="right">
          月間検索数の取得に問題が発生しました。<br />
          再度実行しても取得できない場合、お手数ですがサポートまでご連絡ください。
        </Alert>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import TitleSub from '@/components/TitleSub';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    project: {
      type: Object,
      required: true
    },
    isProcessing: {
      type: Boolean,
      required: true
    },
    hasError: {
      type: Boolean,
      required: false
    }
  },
  components: {
    Alert,
    Button,
    Icon2,
    TitleSub
  },
  data() {
    return {
      error: {
        input: ''
      },
      limit: 20,
      params: {
        input: '',
        tags: []
      },
      isEdited: {
        input: false
      },
      isSubmitted: false
    };
  },
  computed: {
    total() {
      return this.params.tags.length;
    },
    isDisabled() {
      return this.isError || this.isProcessing || this.isSubmitted;
    },
    isError() {
      // 入力値にエラーがある or 何も入力していない場合はtrue
      // 入力値が空でタグがある場合はfalse
      if (
        (this.params.input && this.error.input) ||
        (!this.params.input && !this.params.tags.length) ||
        this.isOver
      ) {
        return true;
      } else {
        return false;
      }
    },
    isOver() {
      return this.total > this.limit;
    }
  },
  watch: {
    params: {
      handler() {
        this.isSubmitted = false;
      },
      deep: true
    },
    // 検索ボリュームの取得でAPIエラーが発生した場合、アラートが消えた後ボタンを有効にする
    hasError: {
      handler(value) {
        if (!value) {
          this.isSubmitted = false;
        }
      }
    },
    'params.input': {
      handler() {
        // カンマは区切り文字に使うため、取り除いた状態でバリデートする
        // 「  ,  」などをエラーにしたい
        const value = this.trimKeyword(this.params.input.replace(/\n|,/g, ''));
        this.error.input = new Validation().input().test(value);
        this.isEdited.input = value ? true : false;
      },
      immediate: true
    }
  },
  methods: {
    addTag(input) {
      const keywords = input
        .split(/\n|,/) //改行 or カンマ区切りで配列にする
        .map(keyword => this.trimKeyword(keyword))
        .filter(keyword => keyword); //空のアイテムを除外

      for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i];
        // 重複するキーワードを除外
        if (this.params.tags.indexOf(keyword) === -1) {
          this.params.tags.push(keyword);
        }
      }
      this.params.input = '';
    },
    enter(e) {
      if (e.keyCode !== 13) {
        return;
      }
      if (this.params.input) {
        this.addTag(this.params.input);
      } else {
        // 入力値が空の時のEnterでsubmit処理
        this.submit();
      }
    },
    focusInput() {
      this.$refs.input.focus();
    },
    paste(e) {
      e.preventDefault();
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedData = clipboardData.getData('text');

      // pasteする前に入力していたものがあれば追加する
      this.addTag(this.params.input);
      this.addTag(pastedData);
    },
    removeLastTag() {
      if (this.params.input || !this.params.tags.length) {
        return;
      }
      // 入力値が空で、タグがある場合のみ最後のタグを消す
      this.params.tags.pop();
    },
    removeTag(index) {
      this.params.tags.splice(index, 1);
    },
    submit() {
      // テキストエリアに値を入力している状態でボタンをクリックした場合は、入力値をタグに追加する
      this.addTag(this.params.input);

      if (this.isDisabled) {
        return;
      }
      this.$emit('submit', this.params.tags);

      this.$nextTick(() => {
        // 入力値が変更されるまではボタンを押せないようにする
        this.isSubmitted = true;
      });
    },
    trimKeyword(keyword) {
      return utils.normalizeAllSpaces(
        utils.convertToHalfWidth(keyword.trim().toLowerCase())
      );
    }
  }
};
</script>
